// src/admin/components/BackgroundJobs.jsx
//
// Ticket 82. The Background section on Health: do the scheduled jobs run, and are
// journal entries getting embedded. Every rule (what counts as overdue, what a
// green row means) is in src/admin/lib/backgroundJobs.js and is tested there.
//
// Two reads, both admin-only database functions, both numbers and names only:
//   admin_background_jobs()    one row per job in cron.job: schedule, newest run
//   admin_reflection_states()  five counts and one age. Never reflection text.
// One write, the Retry beside the Failed count (ticket 95):
//   admin_retry_failed_reflections()  puts every failed entry back to pending.
//
// A read that fails says so, in words, in its own box. It never falls back to an
// empty list or zeros, because "nothing is wrong" is the one thing this screen
// must not say by accident.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import {
  EXPECTED_JOBS, jobState, everyText, meaningOfSuccess, missingJobs, jobsNeedingAttention,
  reflectionVerdicts, firstReflectionRow, jobRows,
} from '../lib/backgroundJobs';

const TONE = {
  ok:      { bg: 'var(--feedback-success-bg)', color: 'var(--feedback-success)', border: 'var(--feedback-success)' },
  warn:    { bg: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', border: 'var(--feedback-warning-border)' },
  bad:     { bg: 'var(--feedback-error-bg)', color: 'var(--feedback-error)', border: 'var(--feedback-error)' },
  neutral: { bg: '#f5f0e8', color: 'var(--neutral-600)', border: 'var(--brand-sand)' },
};

const fmt = n => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

function Pill({ tone = 'neutral', children }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <span data-tone={tone} style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-md)', background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Box({ tone, role, children, ...rest }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div role={role} {...rest} style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>
      {children}
    </div>
  );
}

function Count({ label, value, sub, tone, action }) {
  const t = tone && tone !== 'neutral' ? TONE[tone] : null;
  return (
    <div data-count={label} style={{ background: 'var(--brand-white)', border: `1px solid ${t ? t.border : 'var(--brand-sand)'}`, borderRadius: 'var(--radius-lg)', padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: t ? t.color : 'var(--neutral-800)', lineHeight: 1.1, marginTop: 4 }}>{fmt(value)}</div>
      {sub && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 4 }}>{sub}</div>}
      {action}
    </div>
  );
}

function JobRow({ job, isMobile }) {
  const s = jobState(job);
  const known = EXPECTED_JOBS.find(e => e.name === job.job_name);
  return (
    <div data-job={job.job_name} data-tone={s.tone} style={{ border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10, background: 'var(--brand-white)' }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: 'var(--neutral-800)' }}>{job.job_name}</div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', marginTop: 2 }}>
            Runs {everyText(job.interval_seconds, job.schedule)}
          </div>
        </div>
        <Pill tone={s.tone}>{s.label}</Pill>
      </div>
      {s.detail && <div style={{ marginTop: 8, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)' }}>{s.detail}</div>}
      {job.last_status === 'failed' && job.last_error && (
        <pre data-last-error style={{ margin: '8px 0 0', padding: '8px 10px', background: '#faf8f4', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-700)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{job.last_error}</pre>
      )}
      {job.last_status === 'succeeded' && job.active !== false && (
        <div style={{ marginTop: 6, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>{meaningOfSuccess(job)}</div>
      )}
      {known?.note && <div style={{ marginTop: 6, fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>{known.note}</div>}
    </div>
  );
}

function MissingRow({ job, isMobile }) {
  return (
    <div data-job={job.name} data-tone="bad" data-missing style={{ border: `1px solid ${TONE.bad.border}`, background: TONE.bad.bg, borderRadius: 'var(--radius-lg)', padding: isMobile ? 12 : 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center' }}>
        <div style={{ fontWeight: 700, color: 'var(--neutral-800)' }}>{job.name}</div>
        <Pill tone="bad">Not scheduled</Pill>
      </div>
      <div style={{ marginTop: 8, fontSize: 'var(--font-size-sm)', color: TONE.bad.color }}>
        This job is not in the schedule on this database, so it is not running. {job.stops}
      </div>
    </div>
  );
}

export default function BackgroundJobs({ isMobile }) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState(null);
  const [jobsError, setJobsError] = useState(null);
  const [refl, setRefl] = useState(null);
  const [reflError, setReflError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryNote, setRetryNote] = useState(null);

  // A newer load supersedes an older one.
  const loadId = useRef(0);

  const load = useCallback(async () => {
    const id = ++loadId.current;
    setLoading(true);
    // One after the other, each as its own checked statement: check:silent-writes
    // counts a database function call as a write and only accepts the answer being
    // destructured with its error right where the call is made.
    const { data: jobData, error: jobErr } = await supabase.rpc('admin_background_jobs');
    const { data: reflData, error: reflErr } = await supabase.rpc('admin_reflection_states');
    if (id !== loadId.current) return;

    if (jobErr) {
      console.error('[Health] jobs failed:', jobErr.message);
      setJobs(null);
      setJobsError(jobErr.message);
    } else {
      const rows = jobRows(jobData);
      setJobs(rows);
      setJobsError(rows ? null : 'The database answered in a shape the screen does not recognise.');
    }

    if (reflErr) {
      console.error('[Health] reflection counts failed:', reflErr.message);
      setRefl(null);
      setReflError(reflErr.message);
    } else {
      const row = firstReflectionRow(reflData);
      setRefl(row);
      setReflError(row ? null : 'The database answered with no counts.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function retryFailed() {
    setRetrying(true);
    setRetryNote(null);
    const { data: retried, error: retryErr } = await supabase.rpc('admin_retry_failed_reflections');
    setRetrying(false);
    if (retryErr) {
      console.error('[Health] retry failed entries failed:', retryErr.message);
      // An error with a code came from the database, and the call rolled back. No code means no answer
      // came back, and the change may already have been made.
      const answered = Boolean(retryErr.code);
      setRetryNote({
        tone: 'bad',
        text: answered
          ? `Couldn't put the failed entries back to waiting. Nothing was changed. (${retryErr.message})`
          : `Couldn't tell whether the retry worked, because no answer came back. The counts below are what the database holds now. (${retryErr.message})`,
      });
      await load();
      return;
    }
    const n = Number(retried);
    setRetryNote({
      tone: 'ok',
      text: n === 0
        ? 'There were no failed entries to retry.'
        : `${n} failed ${n === 1 ? 'entry' : 'entries'} put back to waiting. The next run takes ${n === 1 ? 'it' : 'them'}. An entry OpenAI rejected may fail again.`,
    });
    await load();
  }

  const attention = jobs ? jobsNeedingAttention(jobs) : null;
  const missing = jobs ? missingJobs(jobs) : [];
  const verdicts = reflectionVerdicts(refl);

  return (
    <div data-background style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '20px 22px', marginBottom: 18 }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 6 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--neutral-800)', margin: 0 }}>Background</h2>
        <button type="button" onClick={load} disabled={loading} style={{ padding: isMobile ? '0 16px' : '7px 14px', minHeight: isMobile ? 44 : undefined, background: 'var(--brand-white)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.55 : 1, fontFamily: 'var(--font-body)' }}>
          {loading ? 'Checking…' : 'Check again'}
        </button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', lineHeight: 1.5 }}>
        Jobs that run on a schedule by themselves. If one stops, nothing else tells you: thoughts stop expiring, covers stop caching, journal entries stop becoming searchable. A job is flagged when its last run failed or it has not run in twice its usual gap.
      </p>

      <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 8px' }}>Scheduled jobs</h3>
      {loading && !jobs && !jobsError ? (
        <div style={{ padding: '14px 0', color: 'var(--neutral-400)' }}>Loading…</div>
      ) : jobsError ? (
        <Box tone="bad" role="alert" data-jobs-error>
          Couldn&apos;t read the scheduled jobs: {jobsError}. That is not the same as everything being fine.
        </Box>
      ) : (
        <>
          <div style={{ marginBottom: 10 }}>
            {attention === 0
              ? <Box tone="ok" role="status" data-jobs-summary>Every job is running on time.</Box>
              : <Box tone="bad" role="status" data-jobs-summary>{attention === 1 ? '1 job needs attention.' : `${attention} jobs need attention.`}</Box>}
          </div>
          {missing.map(m => <MissingRow key={m.name} job={m} isMobile={isMobile} />)}
          {jobs.map(j => <JobRow key={j.job_id ?? j.job_name} job={j} isMobile={isMobile} />)}
        </>
      )}

      <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: '18px 0 8px' }}>Journal entries and matching</h3>
      {loading && !refl && !reflError ? (
        <div style={{ padding: '14px 0', color: 'var(--neutral-400)' }}>Loading…</div>
      ) : reflError ? (
        <Box tone="bad" role="alert" data-reflections-error>
          Couldn&apos;t count the journal entries: {reflError}. A blank here means unknown, not zero.
        </Box>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12 }}>
            <Count label="Waiting" value={refl.waiting} tone={verdicts.waiting.tone} sub={verdicts.waiting.text} />
            <Count label="Failed" value={refl.failed} tone={verdicts.failed.tone} sub={verdicts.failed.text}
              action={refl.failed > 0 && (
                <button type="button" onClick={retryFailed} disabled={retrying} data-retry-failed style={{ marginTop: 8, padding: isMobile ? '0 16px' : '6px 12px', minHeight: isMobile ? 44 : undefined, background: 'var(--brand-white)', color: 'var(--neutral-700)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: retrying ? 'not-allowed' : 'pointer', opacity: retrying ? 0.55 : 1, fontFamily: 'var(--font-body)' }}>
                  {retrying ? 'Retrying…' : 'Retry'}
                </button>
              )} />
            <Count label="Embedded" value={refl.embedded} sub="Searchable for matching." />
            <Count label="Nothing to embed" value={refl.nothing_to_embed} sub="No text yet. Left alone on purpose, so they are not counted as waiting." />
          </div>
          {retryNote && <div style={{ marginTop: 10 }}><Box tone={retryNote.tone} role={retryNote.tone === 'bad' ? 'alert' : 'status'} data-retry-note>{retryNote.text}</Box></div>}
          <p style={{ margin: '10px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
            Counts only. This screen never shows what a reader wrote.{refl.failed > 0 && ' Retry puts every failed entry back to waiting. An entry OpenAI rejected may fail again.'}
          </p>
        </>
      )}
    </div>
  );
}
